import { Link } from 'react-router-dom'
import { MapPin, Images, FileText } from 'lucide-react'
import type { Trip } from '../api/types'
import { deriveTripPhase } from '../hooks/useTripPhase'
import { formatDateRange } from '../lib/dates'
import { StatusPill } from './StatusPill'

const COVER_GRADIENTS = [
  'linear-gradient(135deg, #2D6A4F 0%, #81B29A 100%)',
  'linear-gradient(135deg, #E07A5F 0%, #B7791F 100%)',
  'linear-gradient(135deg, #3182CE 0%, #6B46C1 100%)',
  'linear-gradient(135deg, #319795 0%, #2D6A4F 100%)',
]

function coverFor(trip: Trip): string {
  const i = Math.abs(hash(trip.id)) % COVER_GRADIENTS.length
  return COVER_GRADIENTS[i]
}

function hash(s: string): number {
  let h = 0
  for (let i = 0; i < s.length; i++) h = (h << 5) - h + s.charCodeAt(i)
  return h
}

export function TripCard({
  trip,
  pointCount = 0,
}: {
  trip: Trip
  pointCount?: number
}) {
  const phase = deriveTripPhase(trip)
  return (
    <Link
      to={phase === 'live' ? `/trips/${trip.id}/live` : `/trips/${trip.id}`}
      className="group block overflow-hidden rounded-st-lg border border-st-line bg-st-surface shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
    >
      <div
        className="relative aspect-[16/9] w-full"
        style={{ background: trip.cover_url ? undefined : coverFor(trip) }}
      >
        {trip.cover_url && (
          <img src={trip.cover_url} alt="" className="h-full w-full object-cover" />
        )}
        <div className="absolute right-3 top-3">
          <StatusPill phase={phase} />
        </div>
      </div>
      <div className="p-4">
        <h3 className="truncate text-lg font-semibold text-st-text">{trip.title}</h3>
        <p className="mt-0.5 text-sm text-st-muted">{formatDateRange(trip.start_date, trip.end_date)}</p>
        {trip.destination && (
          <p className="mt-2 inline-flex items-center gap-1 rounded-st-full bg-st-surface-2 px-2.5 py-1 text-xs text-st-text">
            <MapPin className="h-3.5 w-3.5" /> {trip.destination}
          </p>
        )}
        <div className="mt-3 flex items-center gap-4 text-xs text-st-muted">
          <span className="inline-flex items-center gap-1">
            <MapPin className="h-3.5 w-3.5" /> {pointCount} places
          </span>
          <span className="inline-flex items-center gap-1">
            <Images className="h-3.5 w-3.5" /> 0 photos
          </span>
          <span className="inline-flex items-center gap-1">
            <FileText className="h-3.5 w-3.5" /> 0 docs
          </span>
        </div>
      </div>
    </Link>
  )
}
